import { Component, OnInit, OnDestroy } from '@angular/core';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment-timezone';
import { ToastrService } from 'ngx-toastr';
import { LevelOfServiceService } from '../level-of-service/service/level-of-service.service';
import { ZonesService } from '../zones/zones-service/zones.service';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { AvailabilityMatrixService } from './availability-matrix.service';
import { MenuService } from '../shared/layout/menu-service/menu.service';


export interface ScheduleHour {
  hour: number;
  label: string;
}

export interface ScheduleDay {
  isoDay: number;
  name: string;
  dateIso: string;
  dateDisplay: string;
}

export interface ScheduleCell {
  hour: number;
  isoDay: number;
  dateIso: string;
  tripCount: number;
  availability: number;
}

@Component({
  selector: 'app-availability-matrix',
  templateUrl: './availability-matrix.component.html',
  styleUrls: ['./availability-matrix.component.css']
})
export class AvailabilityMatrixComponent implements OnInit, OnDestroy {
  isAdmin = false;
  timeZone = 'America/New_York';
  selectedDate: any = null;
  selectedLos: string | null = null;
  selectedZoneId: string | null = null;
  levelOfServices: any[] = [];
  zones: any[] = [];
  zoneMap: { [id: string]: any } = {};
  coverageByHour: { [hour: number]: number } = {};
  weekDays: ScheduleDay[] = [];
  weekLabel = '';
  currentWeekStart: any = null;
  currentWeekEnd: any = null;
  hours: ScheduleHour[] = [];
  scheduleGrid: { [hour: number]: { [day: number]: ScheduleCell } } = {};
  isLoading = false;
  selectedZone: any = null;
  selectedPath: any[] = [];
  mapOptions = {
    lat: 33.5362475,
    lng: -111.9267386,
    zoom: 10
  };
  private agmMap: any;
  private tripsLoadToken = 0;

  constructor(
    private sharedDataService: SharedDataService,
    private availabilityMatrixService: AvailabilityMatrixService,
    private levelOfServiceService: LevelOfServiceService,
    private zonesService: ZonesService,
    private toastr: ToastrService,
    private menuService: MenuService
  ) {
    this.hours = Array.from({ length: 24 }, (_, h) => {
      const paddedHour = h < 10 ? '0' + h : '' + h;
      return {
        hour: h,
        label: paddedHour + ':00'
      };
    });
    this.coverageByHour = this.hours.reduce((obj, item) => (obj[item.hour] = 0, obj), {});
  }

  ngOnInit() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((data: any) => {
        if (!data) {
          return;
        }
        this.isAdmin = this.menuService.isAdmin(data) || !!data.isAllowCoverage;
        if (data.timeZone) {
          this.timeZone = data.timeZone;
        }

        const startOfWeek = moment().tz(this.timeZone).startOf('isoWeek');
        const endOfWeek = moment().tz(this.timeZone).endOf('isoWeek');
        this.currentWeekStart = startOfWeek;
        this.currentWeekEnd = endOfWeek;
        this.weekLabel = startOfWeek.format('MM/DD/YYYY') + ' ~ ' + endOfWeek.format('MM/DD/YYYY');
        this.selectedDate = [startOfWeek.toDate(), endOfWeek.toDate()];
        this.weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((name, i) => {
          const dayMoment = moment(startOfWeek).tz(this.timeZone).add(i, 'days');
          return {
            isoDay: i + 1,
            name,
            dateIso: dayMoment.format('YYYY-MM-DD'),
            dateDisplay: dayMoment.format('MM/DD/YYYY')
          };
        });

        this.levelOfServiceService
          .getLevelOfServices({ status: 'active' })
          .pipe(takeUntil(componentDestroyed(this)))
          .subscribe(
            (losData: any) => {
              this.levelOfServices = losData || [];
            },
            () => {
              this.levelOfServices = [];
            }
          );

        this.zonesService
          .getZones({ status: 'active' })
          .pipe(takeUntil(componentDestroyed(this)))
          .subscribe(
            (zoneData: any) => {
              this.zones = zoneData || [];
              this.zoneMap = this.zones.reduce((obj, item) => (obj[item._id] = item, obj), {});
            },
            () => {
              this.zones = [];
              this.zoneMap = {};
            }
          );

        this.loadTrips();
      });
  }

  ngOnDestroy() {
  }

  onPickerClosed() {
    if (!this.selectedDate) {
      return;
    }
    let picked: any;
    if (Array.isArray(this.selectedDate)) {
      picked = this.selectedDate[0] || this.selectedDate[1];
    } else {
      picked = this.selectedDate;
    }
    if (!picked) {
      return;
    }
    const pickedMoment = moment(picked).tz(this.timeZone);
    const startOfWeek = moment(pickedMoment).startOf('isoWeek');
    const endOfWeek = moment(pickedMoment).endOf('isoWeek');
    const currentStart = this.currentWeekStart ? this.currentWeekStart.format('YYYY-MM-DD') : '';
    if (startOfWeek.format('YYYY-MM-DD') === currentStart) {
      this.selectedDate = [startOfWeek.toDate(), endOfWeek.toDate()];
      this.weekLabel = startOfWeek.format('MM/DD/YYYY') + ' ~ ' + endOfWeek.format('MM/DD/YYYY');
      return;
    }
    this.currentWeekStart = startOfWeek;
    this.currentWeekEnd = endOfWeek;
    this.weekLabel = startOfWeek.format('MM/DD/YYYY') + ' ~ ' + endOfWeek.format('MM/DD/YYYY');
    this.selectedDate = [startOfWeek.toDate(), endOfWeek.toDate()];
    this.weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((name, i) => {
      const dayMoment = moment(startOfWeek).tz(this.timeZone).add(i, 'days');
      return {
        isoDay: i + 1,
        name,
        dateIso: dayMoment.format('YYYY-MM-DD'),
        dateDisplay: dayMoment.format('MM/DD/YYYY')
      };
    });
    this.loadTrips();
  }

  loadTrips() {
    const token = ++this.tripsLoadToken;
    this.scheduleGrid = this.hours.reduce((grid: { [hour: number]: { [day: number]: ScheduleCell } }, hourRow) => {
      grid[hourRow.hour] = this.weekDays.reduce((days: { [day: number]: ScheduleCell }, day) => {
        days[day.isoDay] = {
          hour: hourRow.hour,
          isoDay: day.isoDay,
          dateIso: day.dateIso,
          tripCount: 0,
          availability: 0
        };
        return days;
      }, {});
      return grid;
    }, {});
    this.coverageByHour = this.hours.reduce((obj, item) => (obj[item.hour] = 0, obj), {});
    if (!this.selectedLos || !this.selectedZoneId || !this.currentWeekStart || !this.currentWeekEnd) {
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.availabilityMatrixService
      .getAvailabilityTrips({
        startDate: this.currentWeekStart.toISOString(),
        endDate: this.currentWeekEnd.toISOString(),
        filters: {
          los: this.selectedLos,
          zone: this.selectedZoneId
        }
      })
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data: any) => {
          if (token !== this.tripsLoadToken) {
            return;
          }
          this.isLoading = false;
          const payload = data && data.grid ? data : (data && data.data && data.data.grid ? data.data : data);
          const rows = (payload && payload.grid) || [];
          const apiDays = (payload && payload.days) || [];
          if (apiDays.length) {
            this.weekDays = this.weekDays.map((day, i) => {
              const dateIso = (apiDays[i] && apiDays[i].date) || day.dateIso;
              return {
                ...day,
                dateIso,
                dateDisplay: dateIso.slice(5, 7) + '/' + dateIso.slice(8, 10) + '/' + dateIso.slice(0, 4)
              };
            });
          }
          const coverageByHour = this.hours.reduce((obj: { [hour: number]: number }, item) => (obj[item.hour] = 0, obj), {});
          const scheduleGrid = this.hours.reduce((grid: { [hour: number]: { [day: number]: ScheduleCell } }, hourRow) => {
            grid[hourRow.hour] = this.weekDays.reduce((days: { [day: number]: ScheduleCell }, day) => {
              days[day.isoDay] = {
                hour: hourRow.hour,
                isoDay: day.isoDay,
                dateIso: day.dateIso,
                tripCount: 0,
                availability: 0
              };
              return days;
            }, {});
            return grid;
          }, {});
          rows.forEach((row: any) => {
            const hour = parseInt((row.hour + '').split(':')[0], 10);
            if (isNaN(hour) || hour < 0 || hour > 23 || !scheduleGrid[hour]) {
              return;
            }
            const parsedCoverage = Number(row.coverage);
            const hasCoverage = row.coverage !== null && row.coverage !== undefined && row.coverage !== '' && !isNaN(parsedCoverage) && parsedCoverage > 0;
            coverageByHour[hour] = hasCoverage ? parsedCoverage : 0;
            const counts = row.counts || {};
            const values = row.values || counts.values || {};
            this.weekDays.forEach((day) => {
              const cell = scheduleGrid[hour][day.isoDay];
              if (!cell) {
                return;
              }
              const tripRaw = counts[day.dateIso];
              const tripCount = Number(tripRaw);
              cell.tripCount = tripRaw == null || isNaN(tripCount) ? 0 : tripCount;

              if (hasCoverage) {
                const availRaw = values[day.dateIso];
                const availability = Number(availRaw);
                cell.availability = availRaw == null || isNaN(availability) ? (parsedCoverage - cell.tripCount) : availability;
              } else {
                cell.availability = 0;
              }
            });
          });
          this.coverageByHour = coverageByHour;
          this.scheduleGrid = scheduleGrid;
        },
        (err) => {
          if (token !== this.tripsLoadToken) {
            return;
          }
          this.isLoading = false;
          this.toastr.error(err || 'Unable to load availability trips', 'Error');
        }
      );
  }

  onZoneChange() {
    this.selectedZone = this.selectedZoneId ? this.zoneMap[this.selectedZoneId] : null;
    this.selectedPath = [];
    if (this.selectedZone && this.selectedZone.geo && this.selectedZone.geo.coordinates && this.selectedZone.geo.coordinates[0]) {
      this.selectedPath = this.selectedZone.geo.coordinates[0].map((element:any) => ({ lat: element[1], lng: element[0] }));
      if (this.selectedPath.length) {
        this.mapOptions = {
          lat: this.selectedPath[0].lat,
          lng: this.selectedPath[0].lng,
          zoom: 11
        };
      }
      setTimeout(() => {
        const googleRef = (window as any).google;
        if (!this.agmMap || !this.selectedPath.length || !googleRef || !googleRef.maps) {
          return;
        }
        const bounds = new googleRef.maps.LatLngBounds();
        this.selectedPath.forEach((point) => {
          bounds.extend(new googleRef.maps.LatLng(point.lat, point.lng));
        });
        this.agmMap.fitBounds(bounds);
      }, 0);
    }
    this.loadTrips();
  }

  resetFilters() {
    this.selectedLos = null;
    this.selectedZoneId = null;
    this.selectedZone = null;
    this.selectedPath = [];
    this.loadTrips();
  }

  onMapReady(map: any) {
    this.agmMap = map;
    const googleRef = (window as any).google;
    if (!this.agmMap || !this.selectedPath.length || !googleRef || !googleRef.maps) {
      return;
    }
    const bounds = new googleRef.maps.LatLngBounds();
    this.selectedPath.forEach((point) => {
      bounds.extend(new googleRef.maps.LatLng(point.lat, point.lng));
    });
    this.agmMap.fitBounds(bounds);
  }
}
